import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';
import { LocationSuggestInput } from '../components/LocationSuggestInput';
import { PaginatedListLoading } from '../../../../../components/ui/PaginatedListLoading';

const API_BASE =
  process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type CustomerListRow = {
  id: string;
  email: string;
  status: string;
  createdAt: string;
  customerProfile?: {
    fullName?: string | null;
    phone?: string | null;
    country?: string | null;
    city?: string | null;
    drivingDurationSeconds?: number | null;
    drivingRouteStatus?: string | null;
  } | null;
  totalSpendUsd: number;
  orderCount: number;
};

function driveTimeCell(p: CustomerListRow['customerProfile']): string {
  if (!p) return '—';
  const sec = p.drivingDurationSeconds;
  if (sec == null) {
    if (p.drivingRouteStatus === 'PENDING') return '…';
    return '—';
  }
  return `${Math.round(sec / 60)} min`;
}

type ListResponse = {
  items: CustomerListRow[];
  total: number;
  page: number;
  pageSize: number;
  sort: string;
};

type LocationHints = {
  countries: string[];
  cityRows: Array<{ country: string | null; city: string }>;
};

const SORT_OPTIONS = [
  { value: 'createdAt_desc', key: 'backoffice.customers_sort_created_desc' },
  { value: 'createdAt_asc', key: 'backoffice.customers_sort_created_asc' },
  { value: 'totalSpend_desc', key: 'backoffice.customers_sort_spend_desc' },
  { value: 'totalSpend_asc', key: 'backoffice.customers_sort_spend_asc' },
  { value: 'orderCount_desc', key: 'backoffice.customers_sort_orders_desc' },
  { value: 'orderCount_asc', key: 'backoffice.customers_sort_orders_asc' },
] as const;

const inputClassName =
  'h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none ring-brand-500/30 focus:border-brand-500 focus:ring-2';

const FILTERS_EXPANDED_KEY = 'lewouhmart.workspace.customers.filtersExpanded';

function CustomersPage() {
  const { authorizedFetch } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Math.max(1, Number(searchParams.get('page')) || 1);
  const pageSize = Math.min(
    100,
    Math.max(5, Number(searchParams.get('pageSize')) || 20),
  );
  const sort = searchParams.get('sort') || 'createdAt_desc';

  const [draftQ, setDraftQ] = useState(searchParams.get('q') ?? '');
  const [draftPhone, setDraftPhone] = useState(searchParams.get('phone') ?? '');
  const [draftCountry, setDraftCountry] = useState(
    searchParams.get('country') ?? '',
  );
  const [draftCity, setDraftCity] = useState(searchParams.get('city') ?? '');
  const [draftFrom, setDraftFrom] = useState(
    searchParams.get('registeredFrom') ?? '',
  );
  const [draftTo, setDraftTo] = useState(
    searchParams.get('registeredTo') ?? '',
  );

  const committedKey = useMemo(() => searchParams.toString(), [searchParams]);

  useEffect(() => {
    setDraftQ(searchParams.get('q') ?? '');
    setDraftPhone(searchParams.get('phone') ?? '');
    setDraftCountry(searchParams.get('country') ?? '');
    setDraftCity(searchParams.get('city') ?? '');
    setDraftFrom(searchParams.get('registeredFrom') ?? '');
    setDraftTo(searchParams.get('registeredTo') ?? '');
  }, [committedKey, searchParams]);

  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [locationHints, setLocationHints] = useState<LocationHints>({
    countries: [],
    cityRows: [],
  });
  const [hintsStatus, setHintsStatus] = useState<
    'loading' | 'ready' | 'error'
  >('loading');

  const [filtersExpanded, setFiltersExpanded] = useState(() => {
    try {
      const v = sessionStorage.getItem(FILTERS_EXPANDED_KEY);
      if (v === '0') return false;
      if (v === '1') return true;
    } catch {
      /* ignore */
    }
    return true;
  });

  const toggleFiltersExpanded = () => {
    setFiltersExpanded((open) => {
      const next = !open;
      try {
        sessionStorage.setItem(FILTERS_EXPANDED_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  useEffect(() => {
    setHintsStatus('loading');
    void (async () => {
      try {
        const res = await authorizedFetch(
          `${API_BASE}/auth/workspace/customers/meta/locations`,
        );
        if (!res.ok) {
          setLocationHints({ countries: [], cityRows: [] });
          setHintsStatus('error');
          return;
        }
        const json = (await res.json()) as LocationHints;
        setLocationHints({
          countries: Array.isArray(json.countries) ? json.countries : [],
          cityRows: Array.isArray(json.cityRows) ? json.cityRows : [],
        });
        setHintsStatus('ready');
      } catch {
        setLocationHints({ countries: [], cityRows: [] });
        setHintsStatus('error');
      }
    })();
  }, [authorizedFetch]);

  const citySuggestions = useMemo(() => {
    const rows = locationHints.cityRows;
    const c = draftCountry.trim().toLowerCase();
    const addCities = (list: typeof rows) => {
      const set = new Set<string>();
      for (const r of list) {
        if (r.city?.trim()) set.add(r.city.trim());
      }
      return [...set].sort((a, b) => a.localeCompare(b));
    };
    if (!c.length) return addCities(rows);
    const exact = rows.filter(
      (r) => (r.country?.trim().toLowerCase() ?? '') === c,
    );
    const source =
      exact.length > 0
        ? exact
        : rows.filter((r) => r.country?.toLowerCase().includes(c) ?? false);
    return addCities(source);
  }, [locationHints, draftCountry]);

  const queryString = searchParams.toString();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams(queryString);
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      if (sort) params.set('sort', sort);
      const res = await authorizedFetch(
        `${API_BASE}/auth/workspace/customers?${params.toString()}`,
      );
      if (!res.ok) throw new Error(t('backoffice.customers_error'));
      const json = (await res.json()) as ListResponse;
      setData(json);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('backoffice.customers_error'),
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [authorizedFetch, page, pageSize, queryString, sort, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const totalPages = data
    ? Math.max(1, Math.ceil(data.total / data.pageSize))
    : 1;

  const applyFilters = () => {
    const next = new URLSearchParams(searchParams);
    next.set('page', '1');
    const setOrDelete = (key: string, val: string) => {
      const v = val.trim();
      if (v) next.set(key, v);
      else next.delete(key);
    };
    setOrDelete('q', draftQ);
    setOrDelete('phone', draftPhone);
    setOrDelete('country', draftCountry);
    setOrDelete('city', draftCity);
    setOrDelete('registeredFrom', draftFrom);
    setOrDelete('registeredTo', draftTo);
    setSearchParams(next);
  };

  const resetFilters = () => {
    setDraftQ('');
    setDraftPhone('');
    setDraftCountry('');
    setDraftCity('');
    setDraftFrom('');
    setDraftTo('');
    setSearchParams(new URLSearchParams());
  };

  const setPage = (p: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(Math.max(1, p)));
    setSearchParams(next);
  };

  const setSort = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('sort', value);
    next.set('page', '1');
    setSearchParams(next);
  };

  const setPageSize = (n: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('pageSize', String(n));
    next.set('page', '1');
    setSearchParams(next);
  };

  return (
    <MainLayout
      withSidebar
      sidebarItems={getBackofficeSidebarItems(t)}
      activePath={location.pathname}
    >
      <main className="workspace-page">
        <section>
          <h1 className="workspace-page-title">
            {t('backoffice.customers_title')}
          </h1>
          <p className="workspace-page-subtitle text-slate-600">
            {t('backoffice.customers_subtitle')}
          </p>
        </section>

        <section className="workspace-panel space-y-3">
          <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 sm:p-5">
            <div
              className={[
                'flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4',
                filtersExpanded
                  ? 'mb-4 border-b border-slate-200/80 pb-3'
                  : 'border-b border-slate-200/80 pb-3',
              ].join(' ')}
            >
              <div className="min-w-0 flex-1">
                <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  {t('backoffice.customers_filter_section')}
                </h2>
                {filtersExpanded ? (
                  <p className="mt-1 text-xs text-slate-500">
                    {t('backoffice.customers_filter_section_hint')}
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-slate-500">
                    {t('backoffice.customers_filter_collapsed_hint')}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={toggleFiltersExpanded}
                aria-expanded={filtersExpanded}
                aria-controls="customers-filters-panel"
                className={[
                  'inline-flex shrink-0 items-center gap-2 self-start rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors sm:self-center',
                  filtersExpanded
                    ? 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                    : 'border border-brand-600 bg-brand-600 text-white hover:bg-brand-700',
                ].join(' ')}
              >
                {filtersExpanded
                  ? t('backoffice.customers_filter_hide')
                  : t('backoffice.customers_filter_show')}
                <svg
                  className={`h-4 w-4 shrink-0 opacity-80 transition-transform duration-200 ${filtersExpanded ? 'rotate-180' : ''}`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>

            <div
              id="customers-filters-panel"
              hidden={!filtersExpanded}
              className={filtersExpanded ? 'space-y-4' : 'hidden'}
            >
            <div className="grid grid-cols-1 gap-x-5 gap-y-4 lg:grid-cols-12 lg:items-end">
              <label className="flex flex-col gap-1.5 lg:col-span-4">
                <span className="text-xs font-semibold text-slate-700">
                  {t('backoffice.customers_filter_search')}
                </span>
                <input
                  value={draftQ}
                  onChange={(e) => setDraftQ(e.target.value)}
                  className={inputClassName}
                  placeholder={t('backoffice.customers_filter_search_ph')}
                  autoComplete="off"
                />
              </label>
              <label className="flex flex-col gap-1.5 lg:col-span-2">
                <span className="text-xs font-semibold text-slate-700">
                  {t('backoffice.customers_filter_phone')}
                </span>
                <input
                  value={draftPhone}
                  onChange={(e) => setDraftPhone(e.target.value)}
                  className={inputClassName}
                  placeholder={t('backoffice.customers_filter_phone_ph')}
                  autoComplete="off"
                />
              </label>
              <div className="flex flex-col gap-1.5 lg:col-span-3">
                <label
                  htmlFor="customers-filter-country"
                  className="text-xs font-semibold text-slate-700"
                >
                  {t('backoffice.customers_filter_country')}
                </label>
                <LocationSuggestInput
                  id="customers-filter-country"
                  value={draftCountry}
                  onChange={setDraftCountry}
                  suggestions={locationHints.countries}
                  placeholder={t('backoffice.customers_filter_country_ph')}
                  inputClassName={inputClassName}
                  noDataHint={
                    hintsStatus === 'loading'
                      ? t('backoffice.customers_suggest_loading')
                      : hintsStatus === 'error'
                        ? t('backoffice.customers_suggest_error')
                        : t('backoffice.customers_suggest_no_countries')
                  }
                  noMatchesHint={t('backoffice.customers_suggest_no_match')}
                />
              </div>
              <div className="flex flex-col gap-1.5 lg:col-span-3">
                <label
                  htmlFor="customers-filter-city"
                  className="text-xs font-semibold text-slate-700"
                >
                  {t('backoffice.customers_filter_city')}
                </label>
                <LocationSuggestInput
                  id="customers-filter-city"
                  value={draftCity}
                  onChange={setDraftCity}
                  suggestions={citySuggestions}
                  placeholder={t('backoffice.customers_filter_city_ph')}
                  inputClassName={inputClassName}
                  noDataHint={
                    hintsStatus === 'loading'
                      ? t('backoffice.customers_suggest_loading')
                      : hintsStatus === 'error'
                        ? t('backoffice.customers_suggest_error')
                        : t('backoffice.customers_suggest_no_cities')
                  }
                  noMatchesHint={t('backoffice.customers_suggest_no_match')}
                />
              </div>

              <label className="flex flex-col gap-1.5 lg:col-span-6">
                <span className="text-xs font-semibold text-slate-700">
                  {t('backoffice.customers_filter_registered_from')}
                </span>
                <input
                  type="date"
                  value={draftFrom}
                  onChange={(e) => setDraftFrom(e.target.value)}
                  className={inputClassName}
                />
              </label>
              <label className="flex flex-col gap-1.5 lg:col-span-6">
                <span className="text-xs font-semibold text-slate-700">
                  {t('backoffice.customers_filter_registered_to')}
                </span>
                <input
                  type="date"
                  value={draftTo}
                  onChange={(e) => setDraftTo(e.target.value)}
                  className={inputClassName}
                />
              </label>
            </div>

            <div className="mt-5 flex flex-col gap-4 border-t border-slate-200/80 pt-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={applyFilters}
                  className="inline-flex h-10 items-center justify-center rounded-lg bg-brand-600 px-5 text-sm font-semibold text-white hover:bg-brand-700"
                >
                  {t('backoffice.customers_filter_apply')}
                </button>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-300 bg-white px-5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                >
                  {t('backoffice.customers_filter_reset')}
                </button>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-4">
                <label className="flex min-w-0 flex-col gap-1.5 sm:w-56">
                  <span className="text-xs font-semibold text-slate-700">
                    {t('backoffice.customers_sort_label')}
                  </span>
                  <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    className={`${inputClassName} cursor-pointer pr-8`}
                  >
                    {SORT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {t(opt.key)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex min-w-0 flex-col gap-1.5 sm:w-36">
                  <span className="text-xs font-semibold text-slate-700">
                    {t('backoffice.customers_rows_per_page')}
                  </span>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className={`${inputClassName} cursor-pointer pr-8`}
                  >
                    {[10, 20, 50, 100].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
            </div>
          </div>

          {error ? (
            <p className="text-sm text-red-600">{error}</p>
          ) : null}

          {loading ? (
            <PaginatedListLoading
              title={t('backoffice.customers_loading')}
              skeletonRows={5}
            />
          ) : !data?.items.length ? (
            <p className="text-sm text-slate-600">
              {t('backoffice.customers_empty')}
            </p>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 pb-2">
                <span className="text-sm text-slate-600">
                  {t('backoffice.customers_total_count', { count: data.total })}
                </span>
                <span className="text-xs text-slate-500">
                  {t('backoffice.customers_page_range', {
                    from: (data.page - 1) * data.pageSize + 1,
                    to: Math.min(data.page * data.pageSize, data.total),
                    total: data.total,
                  })}
                </span>
              </div>

              <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white/80">
                <table className="min-w-full text-left text-xs sm:text-sm">
                  <thead className="border-b border-slate-200 bg-slate-50/80 text-[11px] uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-3">
                        {t('backoffice.customers_table_col_name')}
                      </th>
                      <th className="px-3 py-3">
                        {t('backoffice.customers_table_col_email')}
                      </th>
                      <th className="px-3 py-3">
                        {t('backoffice.customers_table_col_phone')}
                      </th>
                      <th className="px-3 py-3">
                        {t('backoffice.customers_col_city')}
                      </th>
                      <th className="px-3 py-3">
                        {t('backoffice.customers_col_country')}
                      </th>
                      <th className="px-3 py-3">
                        {t('backoffice.customers_col_drive_time')}
                      </th>
                      <th className="px-3 py-3 text-right">
                        {t('backoffice.customers_col_orders')}
                      </th>
                      <th className="px-3 py-3 text-right">
                        {t('backoffice.customers_col_spend')}
                      </th>
                      <th className="px-3 py-3">
                        {t('backoffice.customers_table_col_status')}
                      </th>
                      <th className="px-3 py-3">
                        {t('backoffice.customers_col_actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.items.map((cust) => {
                      const isBlocked = (cust.status ?? '').toUpperCase() === 'BLOCKED';
                      const statusClass = isBlocked
                        ? 'rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-rose-800'
                        : 'rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-800';
                      return (
                        <tr
                          key={cust.id}
                          className="hover:bg-brand-500/5 cursor-pointer transition-colors"
                          onClick={() =>
                            navigate(
                              `/workspace/customers/${cust.id}${location.search ? `?return=${encodeURIComponent(location.search)}` : ''}`,
                            )
                          }
                        >
                        <td className="px-3 py-3 font-semibold text-slate-900">
                          {cust.customerProfile?.fullName || '—'}
                        </td>
                        <td className="px-3 py-3 text-slate-700">{cust.email}</td>
                        <td className="px-3 py-3 text-slate-600">
                          {cust.customerProfile?.phone || '—'}
                        </td>
                        <td className="px-3 py-3 text-slate-600">
                          {cust.customerProfile?.city || '—'}
                        </td>
                        <td className="px-3 py-3 text-slate-600">
                          {cust.customerProfile?.country || '—'}
                        </td>
                        <td className="px-3 py-3 text-slate-600">
                          {driveTimeCell(cust.customerProfile)}
                        </td>
                        <td className="px-3 py-3 text-right font-medium text-slate-900">
                          {cust.orderCount}
                        </td>
                        <td className="px-3 py-3 text-right font-medium text-slate-900">
                          USD{' '}
                          {cust.totalSpendUsd.toLocaleString(undefined, {
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td className="px-3 py-3">
                          <span className={statusClass}>{cust.status}</span>
                        </td>
                        <td className="px-3 py-3">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                `/workspace/customers/${cust.id}${location.search ? `?return=${encodeURIComponent(location.search)}` : ''}`,
                              );
                            }}
                            className="text-xs font-semibold text-brand-600 hover:text-brand-700"
                          >
                            {t('backoffice.customers_open_detail')}
                          </button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                <span className="text-xs text-slate-500">
                  {t('backoffice.customers_page_of', {
                    page: data.page,
                    pages: totalPages,
                  })}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={data.page <= 1}
                    onClick={() => setPage(data.page - 1)}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                  >
                    {t('backoffice.customers_page_prev')}
                  </button>
                  <button
                    type="button"
                    disabled={data.page >= totalPages}
                    onClick={() => setPage(data.page + 1)}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                  >
                    {t('backoffice.customers_page_next')}
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </main>
    </MainLayout>
  );
}

export default CustomersPage;
